import * as docx from "docx";
import getWordCount from "@cclib/functions/quill/getWordCount.js";

export const UNASSIGNED_SPEAKER_ID = "__unassigned__";

/**
 * Resolve event speakers to normalized { id, name } entries.
 * @param {Array} eventSpeakers
 * @returns {Array<{id: string|null, name: string}>}
 */
function normalizeEventSpeakers(eventSpeakers) {
    if (!eventSpeakers || !Array.isArray(eventSpeakers) || eventSpeakers.length === 0) {
        return [];
    }

    return eventSpeakers
        .filter((s) => s != null)
        .map((s) => {
            if (typeof s === "string") {
                return { id: null, name: s };
            }
            return {
                id: s.id ?? null,
                name: (s.name ?? "").toString(),
            };
        })
        .filter((s) => s.name);
}

/**
 * Find row key for a speaker reference on an event.
 * @param {{id: string|null, name: string}} ref
 * @param {Map<string, object>} rowMap
 * @returns {string|null}
 */
function resolveRowKey(ref, rowMap) {
    if (ref.id && rowMap.has(ref.id)) {
        return ref.id;
    }
    for (const [key, row] of rowMap) {
        if (row.name.toLowerCase() === ref.name.toLowerCase()) {
            return key;
        }
    }
    return null;
}

/**
 * Build speaker metrics from event groups and project speakers.
 * @param {Array} eventGroups
 * @param {Array} speakers
 * @returns {object}
 */
export function buildSpeakerMetrics(eventGroups, speakers) {
    const eventGroupMeta = eventGroups.map((eg) => ({
        id: eg.id,
        name: eg.name || "Untitled",
    }));

    const groupCount = eventGroupMeta.length;
    const emptyGroupStats = () =>
        Array.from({ length: groupCount }, () => ({ events: 0, words: 0 }));

    const rowMap = new Map();

    for (const speaker of speakers) {
        rowMap.set(speaker.id, {
            speakerId: speaker.id,
            name: speaker.name,
            colour: speaker.colour,
            perGroup: emptyGroupStats(),
            totalEvents: 0,
            totalWords: 0,
        });
    }

    rowMap.set(UNASSIGNED_SPEAKER_ID, {
        speakerId: UNASSIGNED_SPEAKER_ID,
        name: null,
        colour: null,
        perGroup: emptyGroupStats(),
        totalEvents: 0,
        totalWords: 0,
    });

    const groupTotals = emptyGroupStats();
    let grandTotalEvents = 0;
    let grandTotalWords = 0;

    eventGroups.forEach((eventGroup, groupIndex) => {
        const events = eventGroup.events || [];
        let groupEvents = 0;
        let groupWords = 0;

        events.forEach((event) => {
            const wordCount = getWordCount(event.text || "");
            const normalizedSpeakers = normalizeEventSpeakers(event.speakers);

            groupEvents += 1;
            groupWords += wordCount;

            if (normalizedSpeakers.length === 0) {
                const row = rowMap.get(UNASSIGNED_SPEAKER_ID);
                row.perGroup[groupIndex].events += 1;
                row.perGroup[groupIndex].words += wordCount;
                row.totalEvents += 1;
                row.totalWords += wordCount;
                return;
            }

            const creditedKeys = new Set();
            let matchedAny = false;

            normalizedSpeakers.forEach((ref) => {
                const key = resolveRowKey(ref, rowMap);
                if (!key || key === UNASSIGNED_SPEAKER_ID) {
                    return;
                }
                if (creditedKeys.has(key)) {
                    return;
                }
                creditedKeys.add(key);
                matchedAny = true;
                const row = rowMap.get(key);
                row.perGroup[groupIndex].events += 1;
                row.perGroup[groupIndex].words += wordCount;
                row.totalEvents += 1;
                row.totalWords += wordCount;
            });

            if (!matchedAny) {
                const row = rowMap.get(UNASSIGNED_SPEAKER_ID);
                row.perGroup[groupIndex].events += 1;
                row.perGroup[groupIndex].words += wordCount;
                row.totalEvents += 1;
                row.totalWords += wordCount;
            }
        });

        groupTotals[groupIndex] = { events: groupEvents, words: groupWords };
        grandTotalEvents += groupEvents;
        grandTotalWords += groupWords;
    });

    const rows = [];
    for (const speaker of speakers) {
        if (rowMap.has(speaker.id)) {
            rows.push(rowMap.get(speaker.id));
        }
    }
    rows.push(rowMap.get(UNASSIGNED_SPEAKER_ID));

    return {
        eventGroups: eventGroupMeta,
        rows,
        groupTotals,
        grandTotals: { events: grandTotalEvents, words: grandTotalWords },
    };
}

function cellParagraph(text, { bold = false } = {}) {
    return new docx.TableCell({
        children: [
            new docx.Paragraph({
                children: [
                    new docx.TextRun({
                        text: String(text),
                        bold,
                    }),
                ],
            }),
        ],
    });
}

function headerCell(text) {
    return new docx.TableCell({
        children: [new docx.Paragraph({ text: String(text) })],
        shading: { fill: "E8E8E8" },
    });
}

/**
 * Generate a Speaker Metrics docx Blob.
 * @param {{ projectName: string, authorEmail: string, metrics: object, labels: object }} options
 * @returns {Promise<Blob>}
 */
export async function generateSpeakerMetricsDocx({
    projectName,
    authorEmail,
    metrics,
    labels,
}) {
    const {
        subtitle = "Speaker Metrics",
        authorLine = "",
        speakerHeader = "Speakers",
        eventsSuffix = (name) => `${name} Events`,
        wordsSuffix = (name) => `${name} Words`,
        totalEventsHeader = "Total Events",
        totalWordsHeader = "Total Words",
        totalsRow = "Totals",
        unassignedRow = "Unassigned",
    } = labels;

    const titlePageSection = {
        properties: {},
        children: [
            new docx.Paragraph({
                text: projectName || "Untitled",
                heading: docx.HeadingLevel.TITLE,
                alignment: docx.AlignmentType.CENTER,
            }),
            new docx.Paragraph({
                text: subtitle,
                heading: docx.HeadingLevel.HEADING_2,
                alignment: docx.AlignmentType.CENTER,
            }),
            new docx.Paragraph({
                alignment: docx.AlignmentType.CENTER,
                children: [
                    new docx.TextRun({
                        text: authorLine || `By: ${authorEmail || ""}`.trim(),
                    }),
                ],
            }),
        ],
    };

    const headerCells = [headerCell(speakerHeader)];
    metrics.eventGroups.forEach((eg) => {
        headerCells.push(headerCell(eventsSuffix(eg.name)));
        headerCells.push(headerCell(wordsSuffix(eg.name)));
    });
    headerCells.push(headerCell(totalEventsHeader));
    headerCells.push(headerCell(totalWordsHeader));

    const tableRows = [
        new docx.TableRow({
            children: headerCells,
            tableHeader: true,
            cantSplit: true,
        }),
    ];

    metrics.rows.forEach((row) => {
        const displayName =
            row.speakerId === UNASSIGNED_SPEAKER_ID
                ? unassignedRow
                : row.name || unassignedRow;
        const cells = [cellParagraph(displayName)];
        row.perGroup.forEach((stats) => {
            cells.push(cellParagraph(stats.events));
            cells.push(cellParagraph(stats.words));
        });
        cells.push(cellParagraph(row.totalEvents));
        cells.push(cellParagraph(row.totalWords));
        tableRows.push(
            new docx.TableRow({
                children: cells,
                cantSplit: true,
            })
        );
    });

    const totalsCells = [cellParagraph(totalsRow, { bold: true })];
    metrics.groupTotals.forEach((stats) => {
        totalsCells.push(cellParagraph(stats.events));
        totalsCells.push(cellParagraph(stats.words));
    });
    totalsCells.push(cellParagraph(metrics.grandTotals.events));
    totalsCells.push(cellParagraph(metrics.grandTotals.words));
    tableRows.push(
        new docx.TableRow({
            children: totalsCells,
            cantSplit: true,
        })
    );

    const tableSection = {
        properties: {
            page: {
                size: {
                    orientation: docx.PageOrientation.LANDSCAPE,
                },
            },
        },
        children: [
            new docx.Table({
                width: { size: 100, type: docx.WidthType.PERCENTAGE },
                rows: tableRows,
            }),
        ],
    };

    const doc = new docx.Document({
        creator: "Closed Caption Creator",
        title: `${projectName || "Untitled"} - ${subtitle}`,
        sections: [titlePageSection, tableSection],
    });

    return docx.Packer.toBlob(doc);
}
